import { CommonModule } from '@angular/common'
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { NbIconModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import { AuthService } from '../../../auth/services/auth.service'
import { Workspace } from '../../../workspaces/interfaces/workspace.interface'
import { WorkspaceService } from '../../../workspaces/services/workspace.service'

@Component({
  selector: 'ngx-workspace-switcher',
  templateUrl: './workspace-switcher.component.html',
  styleUrls: ['./workspace-switcher.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NbIconModule]
})
export class WorkspaceSwitcherComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput', { static: false }) searchInput: ElementRef

  // Component lifecycle
  private destroy$ = new Subject<void>()

  // Dropdown state
  isDropdownOpen = false

  // Search functionality
  searchTerm = ''
  searchTerm$ = new Subject<string>()

  // Workspace data
  workspaces: Workspace[] = []
  filteredWorkspaces: Workspace[] = []
  currentWorkspace: Workspace

  // Pagination
  currentPage = 0
  hasMoreWorkspaces = true
  isLoading = false

  constructor(private workspaceService: WorkspaceService, private authService: AuthService, private router: Router) {}

  ngOnInit(): void {
    // Initialize current workspace
    this.currentWorkspace = this.authService.currentWorkspace

    // Listen for workspace changes
    this.authService.currentWorkspaceChange.pipe(takeUntil(this.destroy$)).subscribe((workspace) => {
      this.currentWorkspace = workspace
    })

    // Setup search with debounce
    this.searchTerm$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((term) => {
      this.performSearch(term)
    })

    // Load initial workspaces
    this.loadWorkspaces()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /**
   * Handle clicks outside the component to close dropdown
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement
    const switcherElement = target.closest('.workspace-switcher')

    if (!switcherElement && this.isDropdownOpen) {
      this.closeDropdown()
    }
  }

  /**
   * Toggle dropdown visibility
   */
  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen

    if (this.isDropdownOpen) {
      // Reset search and reload workspaces when opening
      this.searchTerm = ''
      this.resetWorkspacesList()
      this.loadWorkspaces()

      // Focus search input after dropdown opens
      setTimeout(() => {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.focus()
        }
      }, 150)
    }
  }

  /**
   * Close dropdown
   */
  closeDropdown(): void {
    this.isDropdownOpen = false
  }

  /**
   * Handle search input
   */
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement
    this.searchTerm = target.value
    this.searchTerm$.next(this.searchTerm)
  }

  /**
   * Clear search input
   */
  clearSearch(): void {
    this.searchTerm = ''
    this.searchTerm$.next('')
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus()
    }
  }

  /**
   * Perform workspace search
   */
  private performSearch(term: string): void {
    this.resetWorkspacesList()
    this.loadWorkspaces(term)
  }

  /**
   * Reset workspaces list for new search
   */
  private resetWorkspacesList(): void {
    this.workspaces = []
    this.filteredWorkspaces = []
    this.currentPage = 0
    this.hasMoreWorkspaces = true
  }

  /**
   * Load workspaces from API
   */
  private loadWorkspaces(searchTerm?: string): void {
    if (this.isLoading || !this.hasMoreWorkspaces) return

    this.isLoading = true
    this.currentPage++

    this.workspaceService
      .getWorkspaces(null, this.currentPage, searchTerm || null)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const newWorkspaces = response.data || []

          // Filter out current workspace from the list
          const filteredNewWorkspaces = newWorkspaces.filter((workspace) => workspace.id !== this.currentWorkspace?.id)

          // Add new workspaces to the list
          this.workspaces = [...this.workspaces, ...filteredNewWorkspaces]
          this.filteredWorkspaces = [...this.workspaces]

          // Check if we have more pages
          this.hasMoreWorkspaces = response.current_page < response.last_page
          this.isLoading = false
        },
        error: () => {
          this.isLoading = false
        }
      })
  }

  /**
   * Load more workspaces when scrolling
   */
  onScroll(event: Event): void {
    const element = event.target as HTMLElement
    const threshold = 100

    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
      this.loadWorkspaces(this.searchTerm || undefined)
    }
  }

  /**
   * Switch to selected workspace
   */
  switchWorkspace(workspace: Workspace): void {
    if (workspace.id === this.currentWorkspace?.id) {
      this.closeDropdown()
      return
    }

    // Get current active view and store it
    const currentActiveView = this.getCurrentActiveView()
    this.authService.setAdminToolsActiveView(currentActiveView)

    // Update current workspace
    this.authService.currentWorkspace = workspace

    // Close dropdown
    this.closeDropdown()

    // Navigate to refresh the current page with new workspace context
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/admin/tools'])
    })
  }

  /**
   * Get current active view from the DOM
   */
  private getCurrentActiveView(): string {
    try {
      // Check which nav item is currently active
      const activeNavItem = document.querySelector('.nav-item.active')
      if (activeNavItem) {
        // Determine which view based on the active nav item
        const navItems = document.querySelectorAll('.nav-item')
        const activeIndex = Array.from(navItems).indexOf(activeNavItem)

        switch (activeIndex) {
          case 0:
            return 'statistics'
          case 1:
            return 'url-shortener'
          case 2:
            return 'file-uploader'
          case 3:
            return 'badge-editor'
          default:
            return 'statistics'
        }
      }

      // Fallback: check which content view is currently visible
      const contentViews = document.querySelectorAll('.content-view')
      const contentViewsArray = Array.from(contentViews)

      for (let view of contentViewsArray) {
        const htmlElement = view as HTMLElement
        const styles = window.getComputedStyle(htmlElement)
        if (styles.display !== 'none' && htmlElement.offsetHeight > 0) {
          if (view.classList.contains('badge-editor-view')) return 'badge-editor'
          if (view.classList.contains('statistics-view')) return 'statistics'
          if (view.classList.contains('url-shortener-view')) return 'url-shortener'
          if (view.classList.contains('file-uploader-view')) return 'file-uploader'
        }
      }
    } catch (error) {
      console.warn('Could not determine current active view:', error)
    }

    return 'statistics' // Default fallback
  }

  /**
   * Check if workspace is currently selected
   */
  isCurrentWorkspace(workspace: Workspace): boolean {
    return workspace.id === this.currentWorkspace?.id
  }

  /**
   * Get workspace display name
   */
  getWorkspaceDisplayName(workspace: Workspace): string {
    return workspace?.name || workspace?.slug || 'Untitled Workspace'
  }

  /**
   * Get workspace initials for avatar fallback
   */
  getWorkspaceInitials(workspace: Workspace): string {
    if (!workspace) return '?'

    const name = this.getWorkspaceDisplayName(workspace)
    const words = name.split(' ').filter((word) => word.length > 0)

    if (words.length === 0) return '?'
    if (words.length === 1) return words[0].charAt(0).toUpperCase()

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase()
  }

  /**
   * Handle image loading errors
   */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement
    img.style.display = 'none'
  }

  /**
   * Track function for workspace list
   */
  trackByWorkspaceId(index: number, workspace: Workspace): number {
    return workspace.id
  }
}
